import {Box, Typography} from '@mui/material';
import {ReactComponent as ExportIcon} from '../../../assests/svg/export_icon.svg';
import {t} from 'i18next';
import AurAlertCarousel from '../../common/aur-alert/AurAlertCarousel';
import {useEffect, useState} from 'react';
import {
  getHostingSummaryAlerts,
  updateAlertStatusAsViewed,
} from '../../../api/UserManagementService';
import {setAlerts} from '../../../redux/slices/alertSlice';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {getExportReportXlsx} from '../../../api/ReportingManagement';
import {
  downloadResponseAsXLSX,
  getFilenameFromHeaders,
} from '../../../utils/utils';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';
import AurDataGridErrorOverlay from '../../common/aur-data-grid-error-overlay/AurDataGridErrorOverlay';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_FILE_EXTENTIONS,
  GA_REGIONS,
} from '../../../constants/analyticsConstants';
import {useAnalytics} from '../../../hooks/useAnalytics';
import AurButton from '../../common/aur-button/AurButton';

const HostingUserSummaryReportDetails = () => {
  const alertData = useSelector((state: RootState) => state.alerts);
  const dispatch = useDispatch();
  const {trackGAEvent} = useAnalytics();
  const [toggleAlertState, setToggleAlertState] = useState(false);
  const {clientId, projectId, workspaceId} = useSelector(
    (state: RootState) => state.reports
  );
  const [isAlertDataLoading, setIsAlertDataLoading] = useState(false);
  const [isFailedToLoadAlert, setIsFailedToLoadAlert] =
    useState<boolean>(false);

  const fetchAlertData = async () => {
    setIsAlertDataLoading(true);
    try {
      setIsFailedToLoadAlert(false);
      const responseFetchAlert = await getHostingSummaryAlerts(
        clientId!,
        projectId!,
        workspaceId!
      );
      dispatch(setAlerts(responseFetchAlert?.data?.data));
    } catch (error) {
      setIsFailedToLoadAlert(true);
      setIsAlertDataLoading(false);
    } finally {
      setIsAlertDataLoading(false);
    }
  };
  useEffect(() => {
    if (projectId && clientId && workspaceId) fetchAlertData();
  }, [clientId, toggleAlertState]);

  const handleDownload = async () => {
    try {
      const response = await getExportReportXlsx(
        clientId as string,
        projectId as string,
        workspaceId as string,
        '/hosting-user-summary'
      );
      const fileName =
        getFilenameFromHeaders(response.headers['content-disposition']) ??
        `${workspaceId}_${Date.now()}`;
      downloadResponseAsXLSX(response, fileName, false);
    } catch (error: any) {
      console.error('Error downloading the XLSX:', error?.message);
    }
  };
  const markAlertAsViewed = async (userAlertId: number) => {
    await updateAlertStatusAsViewed(userAlertId);
    setToggleAlertState((prevState) => !prevState);
  };

  return (
    <Box className="hosting-user-summary-details">
      <div className="export-button ">
        <div className="hosting-summary-section">
          <Typography className="hosting-summary-title typo-text subhead1_size">
            {t('reports.hostingUserSummary.label')}
          </Typography>

          <AurButton
            onClick={() => {
              handleDownload();
              trackGAEvent({
                eventName: GA_EVENT_NAMES.EXPORT,
                click: {
                  name: t('gaTracking.reports.exportOption.xlsx'),
                  type: GA_CLICK_TYPES.BUTTON,
                  region: GA_REGIONS.BODY,
                  action: t('gaTracking.reports.exportReport'),
                },
                file_extension: GA_FILE_EXTENTIONS.XLSX,
              });
            }}
            startIcon={<ExportIcon aria-hidden />}
            type="continue"
            size="Medium"
            ariaLabel={t('common.exportXLSX')}
            dataTestId="export-XLSX-btn"
            classNames="typo-text buttonMedium_size"
          >
            {t('common.exportXLSX')}
          </AurButton>
        </div>
      </div>

      {alertData?.alerts?.length > 0 ? (
        <div>
          <span id="hosting-user-summary" className="addUserHint">
            {t('reports.hostingUserSummary.alertHints')}
          </span>
          <div
            className="hosting-summary-alert"
            data-testid="alert"
            aria-describedby="hosting-user-summary"
            aria-roledescription="carousel"
            aria-live="polite"
            tabIndex={0}
          >
            <AurAlertCarousel
              alertsData={
                alertData?.alerts?.map((alert) => ({
                  ...alert,
                  deleteButtonHandler: () =>
                    markAlertAsViewed(alert.userAlertId),
                })) as []
              }
            />
          </div>
        </div>
      ) : isAlertDataLoading ? (
        <div className="alert-loading">
          <AurLoadingIndicator />
        </div>
      ) : null}
      {isFailedToLoadAlert ? (
        <div className="alert-error">
          <AurDataGridErrorOverlay onRefresh={() => fetchAlertData()} />
        </div>
      ) : null}
    </Box>
  );
};

export default HostingUserSummaryReportDetails;
